import {computed} from "vue";
import {storeToRefs} from "pinia";
import {
    EdgeOptions,
    Flowchart,
    FLOWCHART_INSTANCE_OPTIONS,
    FlowchartDocument,
    FlowchartEdge,
    FlowchartInstance,
    FlowchartInstancePath,
    FlowchartMssantePayload,
    PrintOutputFlowchartNode,
    SurveyNodeVerdict
} from "@/core/interfaces/flowchart/flowchart";
import {MSSanteAttachment, MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import {flowchartApi} from "@/container";
import {useSystemStore} from "@/stores/system.store";
import formatHelper from "@/core/helpers/formatHelper";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";

/** An edge of the path, as listed in the message. */
export interface FlowchartMssanteEdge {
    nodeLabel: string;
    label: string;
    slug: string;
    options?: EdgeOptions;
}

/**
 * What the MSSanté message of a terminal node is built from, whether the
 * flowchart is being run or reopened from the patient history.
 */
export interface FlowchartMssanteSource {
    flowchart: Flowchart;
    /** The terminal node reached. */
    node: PrintOutputFlowchartNode;
    /** The patient the flowchart is run for. */
    consumer: Customer | Beneficiary;
    /** The path to the terminal node, as sent to the print endpoint. */
    path: FlowchartInstancePath;
    /** The edges of the path, in order. */
    selectedEdges: FlowchartMssanteEdge[];
    /** The documents attached to the generic message. */
    documents: FlowchartDocument[];
    /**
     * The document classes the path can rebuild, every document when absent: a
     * saved path missing a required option prints blank documents.
     */
    reprintable?: string[];
    /** The products recommended on the terminal node. */
    recommendationIds?: number[];
    /** The pharmacist comment on the terminal node. */
    comment?: string;
    /** The last survey verdict resolved during the flowchart. */
    surveyVerdict?: SurveyNodeVerdict;
}

/**
 * MSSanté prefill and attachments for a terminal node: the message seeded in
 * its `mssante` action payload, with the {{placeholders}} replaced by runtime
 * data, otherwise a generic message built from the flowchart, the patient and
 * the path. Attachments are generated through the print endpoint.
 *
 * Besides the patient, survey and pharmacy placeholders, every option picked
 * on the path is exposed under its field slug (e.g. `{{prescription}}` for the
 * antibiotic chosen on a positive TROD edge), resolved to its label.
 */
const useFlowchartMssante = (source: () => FlowchartMssanteSource | undefined) => {
    const {environment} = storeToRefs(useSystemStore());

    /** Whether the path can rebuild the document. */
    const attachable = (current: FlowchartMssanteSource, document: FlowchartDocument): boolean => {
        return !current.reprintable || current.reprintable.includes(document.class);
    };

    /**
     * The message seeded on the node. It is dropped when it would be sent with
     * blanks: it quotes the survey result and no verdict is known (e.g. reopened
     * from the history, which does not keep it), or its documents cannot be
     * rebuilt (a saved path missing a required option, such as the antibiotic).
     */
    const mssante = computed((): FlowchartMssantePayload | undefined => {
        const current = source();
        const seeded = current?.node.action_payload?.mssante;

        if (!seeded
            || (!current.surveyVerdict && /{{survey_/.test(seeded.subject + seeded.body))
            || !seeded.documents.every(document => attachable(current, document))) {
            return undefined;
        }

        return seeded;
    });

    /** The patient display name. */
    const patientName = (consumer: Customer | Beneficiary): string => {
        return `${consumer.information.first_name} ${consumer.information.last_name}`;
    };

    /**
     * The label of an option value picked on an edge; free text fields are
     * kept as typed.
     */
    const optionLabel = (options: EdgeOptions | undefined, fieldSlug: string, value: string | string[]): string => {
        const values = options?.[fieldSlug]?.values;

        return (Array.isArray(value) ? value : [value])
            .map(slug => values?.find(option => option.slug === slug)?.label ?? slug)
            .join(', ');
    };

    /**
     * The options picked along the path, keyed by field slug. The reserved
     * options of the terminal node are not edge options.
     */
    const edgeOptions = (current: FlowchartMssanteSource): Record<string, string> => {
        return current.path.reduce((placeholders, item) => {
            const options = current.selectedEdges.find(edge => edge.slug === item.slug)?.options;

            Object.entries(item.options ?? {})
                .filter(([fieldSlug]) => !fieldSlug.startsWith('_'))
                .forEach(([fieldSlug, value]) => {
                    placeholders[fieldSlug] = optionLabel(options, fieldSlug, value as string | string[]);
                });

            return placeholders;
        }, {} as Record<string, string>);
    };

    /**
     * Replace every {{placeholder}} of a template with its runtime value.
     */
    const replacePlaceholders = (current: FlowchartMssanteSource, template: string): string => {
        const placeholders: Record<string, string> = {
            ...edgeOptions(current),
            patient_civility: formatHelper.civility(current.consumer.information.gender),
            patient_name: current.consumer.information.name ?? '',
            patient_birth_date: current.consumer.information.formatted_birth_date ?? '',
            survey_score: current.surveyVerdict ? String(current.surveyVerdict.score) : '',
            survey_verdict: current.surveyVerdict?.title ?? '',
            pharmacist_name: environment.value?.user?.name ?? '',
            healthcenter_name: environment.value?.entity?.name ?? '',
        };

        return Object.entries(placeholders).reduce(
            (text, [key, value]) => text.replaceAll(`{{${key}}}`, value),
            template
        );
    };

    /** The message seeded on the node, with its path. */
    const seededPrefill = (current: FlowchartMssanteSource, seeded: FlowchartMssantePayload): MSSantePrefill => {
        const lines = [replacePlaceholders(current, seeded.body)];

        if (current.selectedEdges.length) {
            lines.push('', 'Cheminement :');
            current.selectedEdges.forEach(edge => lines.push(`- ${edge.nodeLabel} : ${edge.label}`));
        }

        return {
            subject: replacePlaceholders(current, seeded.subject),
            body: lines.join('\n'),
        };
    };

    /**
     * The generic message, only offered when a document is meant for the
     * prescriber: an advice sheet listing products (requires_recommendations)
     * is for the patient.
     */
    const genericPrefill = (current: FlowchartMssanteSource): MSSantePrefill | undefined => {
        if (current.node.action_payload.documents.every(document => document.requires_recommendations)) {
            return undefined;
        }

        const patient = patientName(current.consumer);

        const lines = [
            'Bonjour,',
            '',
            `Veuillez trouver ci-joint le résultat du test « ${current.flowchart.name} » réalisé à l'officine pour ${patient}.`,
        ];

        // The path can be empty when the endpoint is opened directly from the node.
        const answeredEdges = current.selectedEdges.filter(edge => edge.label);

        if (answeredEdges.length) {
            lines.push('', 'Cheminement :');
            answeredEdges.forEach(edge => lines.push(`- ${edge.nodeLabel} : ${edge.label}`));
        }

        lines.push('', 'Cordialement');

        return {
            subject: `${current.flowchart.name} — Résultat de ${patient}`,
            body: lines.join('\n'),
        };
    };

    const prefill = computed((): MSSantePrefill | undefined => {
        const current = source();

        if (!current) {
            return undefined;
        }

        return mssante.value ? seededPrefill(current, mssante.value) : genericPrefill(current);
    });

    /**
     * Generate the PDF attachments: the documents declared by the seeded
     * message, otherwise the documents of the generic one, without the advice
     * sheets listing products, which are for the patient.
     */
    const loadAttachments = async (): Promise<MSSanteAttachment[]> => {
        const current = source();

        if (!current) {
            return [];
        }

        const attachments: MSSanteAttachment[] = [];
        const documents = mssante.value?.documents
            ?? current.documents.filter(document => !document.requires_recommendations && attachable(current, document));

        for (const document of documents) {
            const payload = mssante.value
                ? {
                    document: document.class,
                    recommendations: current.recommendationIds,
                    survey_instance_id: current.surveyVerdict?.instance_id,
                }
                : {
                    document: document.class,
                    recommendations: current.recommendationIds,
                    comment: current.comment,
                };

            const response = await flowchartApi().print(current.flowchart.id, current.consumer, payload, current.path);

            attachments.push({
                name: `${document.label}.pdf`,
                blob: new Blob([response], {type: response.type || 'application/pdf'}),
            });
        }

        return attachments;
    };

    return {prefill, loadAttachments};
};

/**
 * The edges of a saved path, followed from the root node: an edge slug is only
 * meaningful among the edges leaving a node, the same one can sit on several
 * branches. Some edges have no slug, so a step may match several edges: the
 * one whose branch holds the rest of the path wins.
 *
 * @param flowchart The flowchart, with its nodes and edges.
 * @param path The saved path.
 * @returns undefined when the flowchart no longer holds the path.
 */
const pathEdges = (flowchart: Flowchart, path: FlowchartInstancePath): FlowchartEdge[] | undefined => {
    const follow = (nodeId: number | undefined, index: number): FlowchartEdge[] | undefined => {
        if (index === path.length) {
            return [];
        }

        for (const edge of flowchart.edges ?? []) {
            if (edge.from_node_id !== nodeId || edge.slug !== path[index].slug) {
                continue;
            }

            const rest = follow(edge.to_node_id, index + 1);

            if (rest) {
                return [edge, ...rest];
            }
        }

        return undefined;
    };

    // The root the path was recorded from, as the flowchart helper picks it.
    return follow(flowchart.nodes?.[0]?.id, 0);
};

/**
 * Rebuild the message source of a flowchart instance saved in the patient
 * history, from the flowchart loaded with its nodes and edges. Only the
 * documents the API deems reprintable are attached, the others would come out
 * blank.
 *
 * @param flowchart The flowchart of the instance, with its nodes and edges.
 * @param instance The saved instance.
 * @param consumer The patient the instance was saved for.
 * @returns undefined when the path no longer leads to a print node.
 */
export const instanceMssanteSource = (
    flowchart: Flowchart,
    instance: FlowchartInstance,
    consumer: Customer | Beneficiary
): FlowchartMssanteSource | undefined => {
    const edges = pathEdges(flowchart, instance.path);
    const node = flowchart.nodes?.find(candidate => candidate.id === edges?.at(-1)?.to_node_id);

    if (!edges || node?.action_type !== 'print') {
        return undefined;
    }

    const terminalOptions = instance.path.at(-1)?.options;

    return {
        flowchart,
        node,
        consumer,
        path: instance.path,
        selectedEdges: edges.map(edge => ({
            nodeLabel: flowchart.nodes.find(candidate => candidate.id === edge.from_node_id)?.label ?? '',
            label: edge.label,
            slug: edge.slug,
            options: edge.options,
        })),
        documents: node.action_payload.documents,
        reprintable: instance.verdict.documents.map(document => document.class),
        recommendationIds: terminalOptions?.[FLOWCHART_INSTANCE_OPTIONS.recommendations] as number[] | undefined,
        comment: terminalOptions?.[FLOWCHART_INSTANCE_OPTIONS.comment] as string | undefined,
    };
};

export default useFlowchartMssante;
